import classNames from 'classnames'
/*
 * Import components and utilities from our extension API. Warning: for demo experiments only.
 */
import { Errors, FormContext, Textfield, Description, Label } from '@bpmn-io/form-js'
import { html, useContext } from 'diagram-js/lib/ui'

export type Item = {
  fileName: string
  id: string
  url: string
  mimeType?: string
}

export const fileDownloadType = 'file-download'

/*
 * This is the rendering part of the custom field. We use `htm` to
 * to render our components without the need of extra JSX transpilation.
 */
export function FileDownloadRenderer(props) {
  //console.log(props)
  // console.log('Rendering FileDownloadRenderer with props:', props)
  const { disabled, errors = [], field } = props
  const { description, id, label, validate = {} } = field
  const { required } = validate
  const { formId } = useContext(FormContext)

  if (!disabled) errors.push('File download component can not be previewed.')
  const errorMessageId = errors.length === 0 ? undefined : `${prefixId(id, formId)}-error-message`

  return html`<div class=${formFieldClasses(fileDownloadType)}>
    <${Label} id=${prefixId(id, formId)} label=${label} required=${required} />
    <div class="pt-3 space-y-3">
      <div class="flex text-sm text-slate-600 items-center justify-between">
        <div class="flex gap-x-2 items-center">
          <svg
            class="w-6 h-6"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M14 2H6C4.9 2 4.01 2.9 4.01 4L4 20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2ZM6 20V4H13V9H18V20H6Z"
              fill="black"
            />
          </svg>

          <div class="text-md hover:text-gray-500">File name</div>
        </div>
        <button class="px-4 cursor-default">
          <svg
            class="w-5 h-5 fill-gray-400"
            width="14"
            height="17"
            viewBox="0 0 14 17"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M14 6H10V0H4V6H0L7 13L14 6ZM0 15V17H14V15H0Z" />
          </svg>
        </button>
      </div>
      <${Description} description=${description} />
      <${Errors} errors=${errors} id=${errorMessageId} />
    </div>
  </div>`
}

/*
 * This is the configuration part of the custom field. It defines
 * the schema type, UI label and icon, palette group, properties panel entries
 * and much more.
 */
FileDownloadRenderer.config = {
  /* we can extend the default configuration of existing fields */
  ...Textfield.config,
  type: fileDownloadType,
  name: 'Download file',
  label: 'Download file',
  organization: '',
  taskId: '',
  iconUrl: 'https://cdn-icons-png.flaticon.com/128/2989/2989976.png',
  propertiesPanelEntries: ['key', 'label', 'description', 'disabled', 'readonly'],
}

// helper //////////////////////

function formFieldClasses(type, { errors = [], disabled = false, readonly = false } = {}) {
  if (!type) {
    throw new Error('type required')
  }

  return classNames('fjs-form-field', `fjs-form-field-${type}`, {
    'fjs-has-errors': errors.length > 0,
    'fjs-disabled': disabled,
    'fjs-readonly': readonly,
  })
}

function prefixId(id, formId) {
  if (formId) {
    return `fjs-form-${formId}-${id}`
  }

  return `fjs-form-${id}`
}
